import React from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { VehicleImageCard } from 'components/vehicle-history';
import { getEasyPayStatus } from '../utils/easyPayStatus';


const formatAmount = (value?: number | null): string => {
  if (value == null || value === 0) return '—';
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

/* ─── Styled components ─────────────────────────────────────────────────── */

const Card = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  padding-right: 16px;
  gap: 16px;

  @media (max-width: 576px) {
    flex-direction: column;
    padding: 16px;
    gap: 12px;
  }
`;

const ImageSection = styled.div`
  flex-shrink: 0;
  width: 200px;
  align-self: flex-start;

  @media (max-width: 576px) {
    width: 100%;
    align-self: auto;
  }
`;

const VehicleIdBold = styled.span`
  font-weight: 700;
`;

const VehicleIdSlug = styled.span`
  font-weight: 400;
`;

const AwardedLabel = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  background: #4caf50;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 12px;
  letter-spacing: 0.3px;
`;

const DetailsSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  min-width: 0;
  margin-left: 40px;

  @media (max-width: 576px) {
    width: 100%;
    margin-left: 0;
  }
`;

const VehicleTitle = styled.div`
  font-weight: 700;
  font-size: 16px;
  color: #000;
  line-height: 100%;
  margin-bottom: 6px;
`;

const GreyLabel = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: #999999;
`;

const GreyValue = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: #999999;
`;

const InfoRow = styled.div`
  display: flex;
  gap: 4px;
  align-items: baseline;
`;

const DataSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  min-width: 0;

  @media (max-width: 576px) {
    width: 100%;
  }
`;

const DataRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  gap: 6px;
`;

const DataLabel = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: #999999;
`;

const DataValue = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: #000;
`;

const StatusValue = styled.span<{ $color?: string }>`
  font-size: 14px;
  font-weight: 500;
  color: ${({ $color }) => $color ?? '#000'};
`;

const ButtonsSection = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;

  @media (max-width: 576px) {
    width: 100%;
    flex-direction: row;
    flex-wrap: wrap;
  }
`;

const ViewButton = styled.button`
  width: 160px;
  height: 32px;
  background: #3994de;
  border: none;
  border-radius: 30px;
  font-size: 14px;
  font-weight: 500;
  color: #fff;
  cursor: pointer;

  &:hover {
    background: #2d7bbf;
  }

  @media (max-width: 576px) {
    flex: 1;
  }
`;

const EasyPayButton = styled.button`
  width: 160px;
  height: 32px;
  background: #d9d9d9;
  border: none;
  border-radius: 30px;
  font-size: 14px;
  font-weight: 500;
  color: #000;
  cursor: pointer;

  &:hover {
    background: #c8c8c8;
  }

  @media (max-width: 576px) {
    flex: 1;
  }
`;

/* ─── Component ─────────────────────────────────────────────────────────── */

type QueryEndpoint = 'getAllBuyerTransactions' | 'getEasyPayBuyerTransactions' | 'getCardBuyerTransactions';
type ReduxTab = 'all' | 'easyPay' | 'card';

interface CompactTransactionCardProps {
  vehicle: any;
  queryEndpoint: QueryEndpoint;
  reduxTab: ReduxTab;
}

const CompactTransactionCard = ({ vehicle }: CompactTransactionCardProps) => {
  const history = useHistory();

  const vehicleTitle = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
  const imageUrl = vehicle.preview_image?.url;
  const auctionId = vehicle.auctionSlug ? `${vehicle.auctionSlug}-${vehicle.orderIndex || '-'}` : null;
  const sellerName = vehicle.user?.dealership_name ?? 'N/A';
  const showAwarded = vehicle.status === 'awarded';

  const paymentStatus = getEasyPayStatus(vehicle);
  const paymentStatusLabel =
    paymentStatus.kind === 'paid' ? 'PAID' :
    paymentStatus.kind === 'pending' ? paymentStatus.label :
    'NOT PAID';
  const paymentStatusColor =
    paymentStatus.kind === 'paid' ? '#4caf50' :
    paymentStatus.kind === 'pending' ? '#CA8D57' :
    '#888888';

  const auctionDate = vehicle.transferRecord?.date_sold
    ? moment(vehicle.transferRecord.date_sold).format('MMM DD, YYYY')
    : vehicle.date_status
    ? moment(vehicle.date_status).format('MMM DD, YYYY')
    : '—';

  const easyPayStatus = getEasyPayStatus(vehicle);
  const showEasyPay = easyPayStatus.kind === 'button';

  return (
    <Card>
      <ImageSection>
        <VehicleImageCard
          imageUrl={imageUrl}
          label={
            auctionId && (
              <>
                <VehicleIdBold>Vehicle ID: </VehicleIdBold>
                <VehicleIdSlug>{auctionId}</VehicleIdSlug>
              </>
            )
          }
          onClick={() => history.push(`/vehicles/${vehicle.id}/view`)}
          badge={showAwarded ? <AwardedLabel>Awarded</AwardedLabel> : undefined}
          borderRadius={10}
        />
      </ImageSection>

      <DetailsSection>
        <VehicleTitle>{vehicleTitle}</VehicleTitle>
        <InfoRow>
          <GreyLabel>VIN</GreyLabel>
          <GreyValue>{vehicle.vin ?? '—'}</GreyValue>
        </InfoRow>
        <InfoRow>
          <GreyLabel>Value Without Miles AD:</GreyLabel>
          <GreyValue>{formatAmount(vehicle.bluebook_mileage_adjustment)}</GreyValue>
        </InfoRow>
        <InfoRow>
          <GreyLabel>Blue Book Lending Value:</GreyLabel>
          <GreyValue>{formatAmount(vehicle.bluebook_value)}</GreyValue>
        </InfoRow>
        <InfoRow>
          <GreyLabel>Mileage:</GreyLabel>
          <GreyValue>{vehicle.mileage?.toLocaleString() ?? '—'}</GreyValue>
        </InfoRow>
      </DetailsSection>

      <DataSection>
        <DataRow>
          <DataLabel>Auction ID</DataLabel>
          <DataValue>{auctionId ?? '—'}</DataValue>
        </DataRow>
        <DataRow>
          <DataLabel>Seller</DataLabel>
          <DataValue>{sellerName}</DataValue>
        </DataRow>
      </DataSection>

      <DataSection>
        <DataRow>
          <DataLabel>Ending Bid</DataLabel>
          <DataValue>{formatAmount(vehicle.vehicle_price)}</DataValue>
        </DataRow>
        <DataRow>
          <DataLabel>Status</DataLabel>
          <StatusValue $color={paymentStatusColor}>{paymentStatusLabel}</StatusValue>
        </DataRow>
        <DataRow>
          <DataLabel>Auction</DataLabel>
          <DataValue>{auctionDate}</DataValue>
        </DataRow>
      </DataSection>

      <ButtonsSection>
        <ViewButton onClick={() => history.push(`/vehicles/${vehicle.id}/view`)}>VIEW</ViewButton>
        {showEasyPay && (
          <EasyPayButton onClick={() => history.push(`/buyer/bids/${vehicle.id}/easy-pay`)}>EASYPAY</EasyPayButton>
        )}
      </ButtonsSection>
    </Card>
  );
};

export default CompactTransactionCard;
